import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import './Availability.css';

const Availability = () => {
  const [filters, setFilters] = useState({
    location_id: '',
    discipline_id: '',
    level_id: '',
    only_available: false,
    search: ''
  });

  // Estados para selección múltiple
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState(new Set());

  // Obtener sedes
  const { data: locations } = useQuery('locations', async () => {
    const response = await api.get('/locations');
    return response.data;
  });

  // Obtener disciplinas
  const { data: disciplines } = useQuery('disciplines', async () => {
    const response = await api.get('/disciplines', { params: { is_active: true } });
    return response.data;
  });

  // Obtener niveles (cuando se selecciona una disciplina), ordenados por order
  const { data: levels } = useQuery(
    ['levels', filters.discipline_id],
    async () => {
      if (!filters.discipline_id) return [];
      const response = await api.get('/levels', { 
        params: { discipline_id: filters.discipline_id, is_active: true } 
      });
      // Ordenar por order (el backend ya debería hacerlo, pero por si acaso)
      const sorted = (response.data || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      return sorted;
    },
    { enabled: !!filters.discipline_id }
  );

  // Obtener disponibilidad
  const { data: availabilityData, isLoading, error } = useQuery(
    ['availability', filters],
    async () => {
      const params = {};
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.discipline_id) params.discipline_id = filters.discipline_id;
      if (filters.level_id) params.level_id = filters.level_id;
      if (filters.only_available) params.only_available = true;
      if (filters.search) params.search = filters.search;

      const response = await api.get('/availability', { params });
      return response.data;
    }
  );

  // Agrupar grupos por sede
  const groupsByLocation = useMemo(() => {
    if (!availabilityData?.groups) return {};

    const grouped = {};
    availabilityData.groups.forEach(group => {
      const locationName = group.location.name;
      if (!grouped[locationName]) {
        grouped[locationName] = {
          location: group.location,
          groups: []
        };
      }
      grouped[locationName].groups.push(group);
    });

    // Ordenar grupos por nombre dentro de cada sede
    Object.keys(grouped).forEach(locationName => {
      grouped[locationName].groups.sort((a, b) => 
        a.group_name.localeCompare(b.group_name)
      );
    });

    return grouped;
  }, [availabilityData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      // Resetear nivel si cambia la disciplina
      ...(field === 'discipline_id' && { level_id: '' })
    }));
  };

  const clearFilters = () => {
    setFilters({
      location_id: '',
      discipline_id: '',
      level_id: '',
      only_available: false,
      search: ''
    });
  };

  const getStatusBadge = (status, available) => {
    if (status === 'full' || available === 0) {
      return <span className="status-badge status-full">❌ Sin cupos</span>;
    } else if (status === 'almost_full') {
      return <span className="status-badge status-almost-full">⚠️ Pocos cupos</span>;
    } else {
      return <span className="status-badge status-available">✅ Disponible</span>;
    }
  };

  const getStatusClass = (status) => {
    if (status === 'full') return 'card-full';
    if (status === 'almost_full') return 'card-almost-full';
    return 'card-available';
  };

  // Función helper para formatear información de categoría
  const formatCategoryInfo = (levelCategory) => {
    if (!levelCategory || levelCategory.category_type === 'skill') {
      return null;
    }
    
    const parts = [];
    
    // Información por año
    if (levelCategory.min_birth_year || levelCategory.max_birth_year) {
      if (levelCategory.min_birth_year === levelCategory.max_birth_year) {
        parts.push(`Año: ${levelCategory.min_birth_year}`);
      } else {
        parts.push(`Años: ${levelCategory.min_birth_year || '?'}${levelCategory.max_birth_year ? `-${levelCategory.max_birth_year}` : '+'}`);
      }
    }
    
    // Información por edad
    if (levelCategory.min_age || levelCategory.max_age) {
      if (levelCategory.min_age === levelCategory.max_age) {
        parts.push(`Edad: ${levelCategory.min_age} años`);
      } else {
        parts.push(`Edad: ${levelCategory.min_age || '?'}${levelCategory.max_age ? `-${levelCategory.max_age} años` : '+ años'}`);
      }
    }
    
    return parts.length > 0 ? parts.join(' | ') : null;
  };

  // Función para formatear texto de un grupo para copiar
  const formatGroupText = (group, location) => {
    let text = `🏀 GRUPO: ${group.group_name}\n\n`;
    
    text += `📚 DISCIPLINA: ${group.discipline}\n`;
    
    // Categoría padre si existe
    if (group.parent_category) {
      const categoryInfo = formatCategoryInfo(group.parent_category);
      text += `📂 CATEGORÍA: ${group.parent_category.name}`;
      if (categoryInfo) {
        text += ` (${categoryInfo})`;
      }
      text += '\n';
    }
    
    // Nivel
    text += `🎯 NIVEL: ${group.level}`;
    if (!group.parent_category && group.level_category && group.level_category.category_type === 'age') {
      const categoryInfo = formatCategoryInfo(group.level_category);
      if (categoryInfo) {
        text += ` (${categoryInfo})`;
      }
    }
    text += '\n\n';
    
    // Sede
    text += `📍 SEDE: ${location.name}\n`;
    if (location.address) {
      text += `   Dirección: ${location.address}\n`;
    }
    if (location.phone) {
      text += `   Teléfono: ${location.phone}\n`;
    }
    text += '\n';
    
    // Horario
    text += `⏰ HORARIO: ${group.schedule.day} ${group.schedule.start_time} - ${group.schedule.end_time}\n`;
    
    // Profesor
    if (group.teacher) {
      text += `👤 PROFESOR: ${group.teacher}\n`;
    }
    text += '\n';
    
    // Disponibilidad
    if (group.available > 0) {
      text += `✅ DISPONIBILIDAD: ${group.available} cupos disponibles (${group.enrolled}/${group.capacity} matriculados)`;
    } else {
      text += `❌ DISPONIBILIDAD: Sin cupos disponibles (${group.enrolled}/${group.capacity} matriculados)`;
    }
    
    return text;
  };

  // Función para copiar al portapapeles con fallback
  const copyToClipboard = async (text) => {
    try {
      // Intentar usar la API moderna
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback para navegadores más antiguos
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          return successful;
        } catch (err) {
          document.body.removeChild(textArea);
          return false;
        }
      }
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
      return false;
    }
  };

  // Función para copiar un grupo individual
  const handleCopyGroup = async (group, location, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    const text = formatGroupText(group, location);
    const success = await copyToClipboard(text);
    
    if (success) {
      toast.success('✓ Información copiada al portapapeles');
    } else {
      toast.error('Error al copiar. Intenta seleccionar y copiar manualmente.');
    }
  };

  // Función para alternar selección de un grupo
  const toggleGroupSelection = (groupId, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!selectionMode) {
      setSelectionMode(true);
    }
    
    setSelectedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Función para copiar todos los grupos seleccionados
  const handleCopySelected = async () => {
    if (selectedGroups.size === 0) {
      toast.warning('No hay grupos seleccionados');
      return;
    }

    // Obtener todos los grupos seleccionados de todos los grupos
    const allGroups = [];
    Object.values(groupsByLocation).forEach(({ groups, location }) => {
      groups.forEach(group => {
        if (selectedGroups.has(group.group_id)) {
          allGroups.push({ group, location });
        }
      });
    });

    // Formatear todos los grupos
    let text = '📋 GRUPOS DISPONIBLES\n';
    text += '='.repeat(50) + '\n\n';
    
    allGroups.forEach(({ group, location }, index) => {
      text += formatGroupText(group, location);
      if (index < allGroups.length - 1) {
        text += '\n' + '-'.repeat(50) + '\n\n';
      }
    });

    const success = await copyToClipboard(text);
    
    if (success) {
      toast.success(`✓ ${selectedGroups.size} grupo(s) copiado(s) al portapapeles`);
    } else {
      toast.error('Error al copiar. Intenta seleccionar y copiar manualmente.');
    }
  };

  // Función para cancelar modo selección
  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedGroups(new Set());
  };

  // Función para seleccionar todos los grupos visibles
  const handleSelectAll = () => {
    const allGroupIds = new Set();
    Object.values(groupsByLocation).forEach(({ groups }) => {
      groups.forEach(group => {
        allGroupIds.add(group.group_id);
      });
    });
    setSelectedGroups(allGroupIds);
  };

  return (
    <div className="availability-page">
      <div className="page-header">
        <h1>📋 Disponibilidad de Cupos</h1>
        <p>Consulta la disponibilidad de cupos en grupos para responder consultas telefónicas</p>
      </div>

      {/* Resumen */}
      {availabilityData?.summary && (
        <div className="availability-summary">
          <div className="summary-card">
            <div className="summary-label">Total Grupos</div>
            <div className="summary-value">{availabilityData.summary.total_groups}</div>
          </div>
          <div className="summary-card summary-success">
            <div className="summary-label">Con Cupos</div>
            <div className="summary-value">{availabilityData.summary.groups_with_availability}</div>
          </div>
          <div className="summary-card summary-danger">
            <div className="summary-label">Sin Cupos</div>
            <div className="summary-value">{availabilityData.summary.groups_full}</div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="location_filter">Sede</label>
            <select
              id="location_filter"
              value={filters.location_id}
              onChange={(e) => handleFilterChange('location_id', e.target.value)}
            >
              <option value="">Todas las sedes</option>
              {locations?.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="discipline_filter">Disciplina</label>
            <select
              id="discipline_filter"
              value={filters.discipline_id}
              onChange={(e) => handleFilterChange('discipline_id', e.target.value)}
            >
              <option value="">Todas las disciplinas</option>
              {disciplines?.map(discipline => (
                <option key={discipline.id} value={discipline.id}>
                  {discipline.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="level_filter">Nivel</label>
            <select
              id="level_filter"
              value={filters.level_id}
              onChange={(e) => handleFilterChange('level_id', e.target.value)}
              disabled={!filters.discipline_id}
            >
              <option value="">Todos los niveles</option>
              {levels
                ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                ?.map(level => {
                let displayText = level.name;
                if (level.category_type === 'age') {
                  const parts = [];
                  if (level.min_birth_year || level.max_birth_year) {
                    if (level.min_birth_year === level.max_birth_year) {
                      parts.push(`Año: ${level.min_birth_year}`);
                    } else {
                      parts.push(`Años: ${level.min_birth_year || '?'}${level.max_birth_year ? `-${level.max_birth_year}` : '+'}`);
                    }
                  }
                  if (level.min_age || level.max_age) {
                    if (level.min_age === level.max_age) {
                      parts.push(`${level.min_age} años`);
                    } else {
                      parts.push(`${level.min_age || '?'}${level.max_age ? `-${level.max_age} años` : '+ años'}`);
                    }
                  }
                  if (parts.length > 0) {
                    displayText += ` (${parts.join(', ')})`;
                  }
                }
                return (
                  <option key={level.id} value={level.id}>
                    {displayText}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="search_filter">Buscar</label>
            <input
              id="search_filter"
              type="text"
              placeholder="Grupo, disciplina, nivel..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="filter-group filter-checkbox">
            <label>
              <input
                type="checkbox"
                checked={filters.only_available}
                onChange={(e) => handleFilterChange('only_available', e.target.checked)}
              />
              Solo con cupos disponibles
            </label>
          </div>

          <div className="filter-group">
            <button className="btn-clear-filters" onClick={clearFilters}>
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Barra de acciones para multiselección */}
      {Object.keys(groupsByLocation).length > 0 && (
        <div className="actions-bar">
          {!selectionMode ? (
            <button 
              className="btn-action btn-select-mode"
              onClick={() => setSelectionMode(true)}
            >
              ✓ Seleccionar Múltiples
            </button>
          ) : (
            <div className="selection-actions">
              <span className="selection-info">
                {selectedGroups.size} grupo(s) seleccionado(s)
              </span>
              <button 
                className="btn-action btn-select-all"
                onClick={handleSelectAll}
              >
                Seleccionar Todos
              </button>
              <button 
                className="btn-action btn-copy-selected"
                onClick={handleCopySelected}
                disabled={selectedGroups.size === 0}
              >
                📋 Copiar Seleccionados ({selectedGroups.size})
              </button>
              <button 
                className="btn-action btn-cancel-selection"
                onClick={handleCancelSelection}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}

      {/* Contenido */}
      {isLoading && (
        <div className="loading-message">
          <p>Cargando disponibilidad...</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>Error al cargar la disponibilidad: {error.message}</p>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {Object.keys(groupsByLocation).length === 0 ? (
            <div className="empty-state">
              <p>No se encontraron grupos con los filtros seleccionados.</p>
            </div>
          ) : (
            Object.entries(groupsByLocation).map(([locationName, data]) => (
              <div key={locationName} className="location-section">
                <div className="location-header">
                  <h2>📍 {locationName}</h2>
                  {data.location.address && (
                    <span className="location-address">{data.location.address}</span>
                  )}
                  {data.location.phone && (
                    <span className="location-phone">📞 {data.location.phone}</span>
                  )}
                </div>

                <div className="groups-grid">
                  {data.groups.map(group => {
                    const isSelected = selectedGroups.has(group.group_id);
                    
                    return (
                      <div
                        key={group.group_id}
                        className={`availability-card ${getStatusClass(group.status)} ${isSelected ? 'card-selected' : ''} ${selectionMode ? 'with-checkbox' : ''}`}
                        onClick={() => {
                          if (selectionMode) {
                            toggleGroupSelection(group.group_id);
                          }
                        }}
                        style={{ cursor: selectionMode ? 'pointer' : 'default' }}
                      >
                        {/* Checkbox de selección (solo en modo selección) */}
                        {selectionMode && (
                          <div 
                            className={`card-selection-checkbox ${isSelected ? 'checked' : ''}`}
                            onClick={(e) => toggleGroupSelection(group.group_id, e)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              onClick={(e) => toggleGroupSelection(group.group_id, e)}
                            />
                            {isSelected && <span className="checkmark">✓</span>}
                          </div>
                        )}
                        
                        <div className="card-header">
                          <h3>{group.group_name}</h3>
                          <div className="card-header-actions">
                            {getStatusBadge(group.status, group.available)}
                            {!selectionMode && (
                              <button
                                className="btn-copy-single"
                                onClick={(e) => handleCopyGroup(group, data.location, e)}
                                title="Copiar información del grupo"
                              >
                                📋 Copiar
                              </button>
                            )}
                          </div>
                        </div>

                      <div className="card-body">
                        <div className="card-info-row">
                          <span className="info-label">Disciplina:</span>
                          <span className="info-value">{group.discipline}</span>
                        </div>
                        
                        {/* Mostrar categoría padre si existe */}
                        {group.parent_category && (
                          <div className="card-info-row category-row">
                            <span className="info-label">📂 Categoría:</span>
                            <span className="info-value category-value">
                              {group.parent_category.name}
                              {formatCategoryInfo(group.parent_category) && (
                                <span className="category-details">
                                  ({formatCategoryInfo(group.parent_category)})
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                        
                        <div className="card-info-row">
                          <span className="info-label">
                            {group.parent_category ? '🎯 Nivel:' : 'Nivel:'}
                          </span>
                          <span className="info-value">
                            {group.level}
                            {!group.parent_category && group.level_category && group.level_category.category_type === 'age' && (() => {
                              const categoryInfo = formatCategoryInfo(group.level_category);
                              return categoryInfo ? ` (${categoryInfo})` : '';
                            })()}
                          </span>
                        </div>
                        
                        {/* Si no tiene padre pero es categoría por edad, mostrar info */}
                        {!group.parent_category && group.level_category && group.level_category.category_type === 'age' && (
                          <div className="card-info-row category-info-box">
                            <span className="info-label">🏷️ Restricción:</span>
                            <span className="info-value category-restriction">
                              {formatCategoryInfo(group.level_category)}
                            </span>
                          </div>
                        )}
                        
                        <div className="card-info-row">
                          <span className="info-label">Horario:</span>
                          <span className="info-value">
                            {group.schedule.day} {group.schedule.start_time} - {group.schedule.end_time}
                          </span>
                        </div>
                        {group.teacher && (
                          <div className="card-info-row">
                            <span className="info-label">Profesor:</span>
                            <span className="info-value">{group.teacher}</span>
                          </div>
                        )}
                      </div>

                      <div className="card-footer">
                        <div className="capacity-info">
                          <div className="capacity-bar">
                            <div
                              className="capacity-fill"
                              style={{ width: `${group.percentage_full}%` }}
                            />
                          </div>
                          <div className="capacity-text">
                            <strong>{group.enrolled}/{group.capacity}</strong> matriculados
                            {group.available > 0 && (
                              <span className="available-text">
                                {' '}• <strong>{group.available}</strong> cupos disponibles
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default Availability;

